<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import ConversationAppointmentsAPI from 'dashboard/api/conversationAppointments';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  appointments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['refresh']);

const isLoading = ref(false);
const showForm = ref(false);
const editingAppointment = ref(null);
const formData = ref({
  title: '',
  event_type: 'event',
  scheduled_at: '',
  end_at: '',
  location: '',
  description: '',
  status: 'scheduled',
});

const statusOptions = [
  { value: 'scheduled', label: 'Agendado', color: 'bg-amber-100 text-amber-700' },
  { value: 'confirmed', label: 'Confirmado', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'completed', label: 'Concluído', color: 'bg-blue-100 text-blue-700' },
  { value: 'cancelled', label: 'Cancelado', color: 'bg-ruby-100 text-ruby-700' },
  { value: 'no_show', label: 'Não Compareceu', color: 'bg-n-alpha-2 text-n-slate-11' },
];

const eventTypeOptions = [
  { value: 'event', label: 'Evento', icon: 'i-lucide-calendar' },
  { value: 'task', label: 'Tarefa', icon: 'i-lucide-check-square' },
];

const formatDate = dateString => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = status => {
  return (
    statusOptions.find(s => s.value === status)?.color ||
    'bg-n-alpha-2 text-n-slate-11'
  );
};

const getStatusLabel = status => {
  return statusOptions.find(s => s.value === status)?.label || status;
};

const resetForm = () => {
  formData.value = {
    title: '',
    event_type: 'event',
    scheduled_at: '',
    end_at: '',
    location: '',
    description: '',
    status: 'scheduled',
  };
  editingAppointment.value = null;
};

const openForm = (appointment = null) => {
  if (appointment) {
    editingAppointment.value = appointment;
    formData.value = {
      title: appointment.title,
      event_type: appointment.event_type || 'event',
      scheduled_at: appointment.scheduled_at
        ? appointment.scheduled_at.slice(0, 16)
        : '',
      end_at: appointment.end_at ? appointment.end_at.slice(0, 16) : '',
      location: appointment.location || '',
      description: appointment.description || '',
      status: appointment.status || 'scheduled',
    };
  } else {
    resetForm();
  }
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const handleSave = async () => {
  if (!formData.value.title || !formData.value.scheduled_at) {
    useAlert('Preencha título e data do agendamento');
    return;
  }

  if (!props.conversationId) {
    useAlert('Selecione uma conversa para criar agendamentos');
    return;
  }

  isLoading.value = true;
  try {
    if (editingAppointment.value) {
      await ConversationAppointmentsAPI.update(
        props.conversationId,
        editingAppointment.value.id,
        formData.value
      );
      useAlert('Agendamento atualizado!');
    } else {
      await ConversationAppointmentsAPI.create(
        props.conversationId,
        formData.value
      );
      useAlert('Agendamento criado!');
    }
    closeForm();
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao salvar agendamento');
  } finally {
    isLoading.value = false;
  }
};

const handleDelete = async appointment => {
  if (!confirm('Excluir este agendamento?')) return;
  try {
    await ConversationAppointmentsAPI.delete(
      props.conversationId || appointment.conversation_id,
      appointment.id
    );
    useAlert('Agendamento excluído!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao excluir agendamento');
  }
};

const handleStatusChange = async (appointment, newStatus) => {
  try {
    await ConversationAppointmentsAPI.update(
      props.conversationId || appointment.conversation_id,
      appointment.id,
      { status: newStatus }
    );
    useAlert('Status atualizado!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao atualizar status');
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        Agendamentos ({{ appointments.length }})
      </h3>
      <Button
        label="Novo Agendamento"
        icon="i-lucide-plus"
        size="sm"
        :disabled="!conversationId"
        @click="openForm()"
      />
    </div>

    <!-- Empty State -->
    <div
      v-if="appointments.length === 0"
      class="flex flex-col items-center justify-center py-12 rounded-xl bg-n-alpha-1"
    >
      <span class="i-lucide-calendar size-12 text-n-slate-10 opacity-50" />
      <p class="mt-4 text-sm text-n-slate-11">Nenhum agendamento</p>
    </div>

    <!-- Appointments List -->
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="appointment in appointments"
        :key="appointment.id"
        class="p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <span
                :class="
                  appointment.event_type === 'task'
                    ? 'i-lucide-check-square'
                    : 'i-lucide-calendar'
                "
                class="size-4 text-n-slate-11"
              />
              <p class="text-sm font-medium text-n-slate-12">
                {{ appointment.title }}
              </p>
              <span
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                :class="getStatusColor(appointment.status)"
              >
                {{ getStatusLabel(appointment.status) }}
              </span>
            </div>
            <div class="mt-2 space-y-1 text-xs text-n-slate-11">
              <p class="flex items-center gap-2">
                <span class="i-lucide-clock size-3.5" />
                {{ formatDate(appointment.scheduled_at) }}
                <span v-if="appointment.end_at">
                  até {{ formatDate(appointment.end_at) }}
                </span>
              </p>
              <p v-if="appointment.location" class="flex items-center gap-2">
                <span class="i-lucide-map-pin size-3.5" />
                {{ appointment.location }}
              </p>
            </div>
            <p
              v-if="appointment.description"
              class="mt-2 text-sm text-n-slate-11"
            >
              {{ appointment.description }}
            </p>
          </div>
          <div class="flex items-center gap-1 ml-4">
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="openForm(appointment)"
            >
              <span class="i-lucide-pencil size-4" />
            </button>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-ruby-600 hover:bg-ruby-500/10 transition-colors"
              @click="handleDelete(appointment)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>

        <!-- Quick Status Change -->
        <div class="flex items-center gap-2 mt-4 pt-3 border-t border-n-weak">
          <span class="text-xs text-n-slate-10">Marcar como:</span>
          <button
            v-for="status in statusOptions.filter(
              s => s.value !== appointment.status
            )"
            :key="status.value"
            class="px-2 py-1 text-xs font-medium rounded-lg transition-colors"
            :class="status.color"
            @click="handleStatusChange(appointment, status.value)"
          >
            {{ status.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Form Modal -->
    <Teleport to="body">
      <div
        v-if="showForm"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="closeForm"
        />
        <div
          class="relative w-full max-w-lg overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">
              {{ editingAppointment ? 'Editar Agendamento' : 'Novo Agendamento' }}
            </h3>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeForm"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 flex flex-col gap-4">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Título *</label>
              <input
                v-model="formData.title"
                type="text"
                class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
              />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-11">Tipo</label>
                <select
                  v-model="formData.event_type"
                  class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                >
                  <option
                    v-for="opt in eventTypeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-11">Status</label>
                <select
                  v-model="formData.status"
                  class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                >
                  <option
                    v-for="opt in statusOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-11">Início *</label>
                <input
                  v-model="formData.scheduled_at"
                  type="datetime-local"
                  class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-11">Término</label>
                <input
                  v-model="formData.end_at"
                  type="datetime-local"
                  class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Local</label>
              <input
                v-model="formData.location"
                type="text"
                placeholder="Endereço ou link da reunião"
                class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Descrição</label>
              <textarea
                v-model="formData.description"
                rows="3"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="closeForm"
            />
            <Button
              :label="isLoading ? 'Salvando...' : 'Salvar'"
              :is-loading="isLoading"
              :disabled="isLoading"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
